import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import LogoSVG from '../components/LogoSVG';

const STATUS_MAP = {
  none:       { label: '상태없음', cls: '' },
  pending:    { label: '보류',     cls: 'pending' },
  in_progress:{ label: '진행중',   cls: 'in_progress' },
  done:       { label: '완료',     cls: 'done' },
};
const DOT_COLORS = { '': '#555', pending: '#ff4d4d', in_progress: '#4d9fff', done: '#28c76f' };

const TRASH_SVG = (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M21.1,5.2h-4.6v-2.3c0-1.3-1-2.3-2.3-2.3h-4.6c-1.3,0-2.3,1-2.3,2.3v2.3H2.9c-.6,0-1.1.5-1.1,1.1s.5,1.1,1.1,1.1h0l1.1,12.6c0,1.9,1.5,3.4,3.4,3.4h9.1c1.9,0,3.4-1.5,3.4-3.3l1.1-12.7h0c.6,0,1.1-.5,1.1-1.1s-.5-1.1-1.1-1.1ZM9.7,2.9h4.6v2.3h-4.6v-2.3ZM17.7,20c0,.6-.5,1.1-1.1,1.1H7.4c-.6,0-1.1-.5-1.1-1.2l-1-12.5h13.5l-1,12.6Z"/>
    <path d="M9.7,18.8c.6,0,1.1-.5,1.1-1.1v-6.8c0-.6-.5-1.1-1.1-1.1s-1.1.5-1.1,1.1v6.8c0,.6.5,1.1,1.1,1.1Z"/>
    <path d="M14.3,18.8c.6,0,1.1-.5,1.1-1.1v-6.8c0-.6-.5-1.1-1.1-1.1s-1.1.5-1.1,1.1v6.8c0,.6.5,1.1,1.1,1.1Z"/>
  </svg>
);

function toTimecode(seconds, fps = 30) {
  const tot = Math.max(0, seconds || 0);
  const h = Math.floor(tot / 3600);
  const m = Math.floor((tot % 3600) / 60);
  const sv = Math.floor(tot % 60);
  const f = Math.floor((tot % 1) * fps);
  return [h, m, sv, f].map(v => String(v).padStart(2, '0')).join(':');
}

function fromTimecode(tc) {
  if (!tc) return 0;
  const parts = tc.split(':').map(Number);
  if (parts.length === 4) return parts[0] * 3600 + parts[1] * 60 + parts[2] + parts[3] / 30;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return 0;
}

const ArrowRight = () => (
  <svg width="7" height="12" viewBox="0 0 7 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="1 1 6 6 1 11"/>
  </svg>
);

export default function VideoViewer() {
  const navigate = useNavigate();
  const { videoId } = useParams();
  const location = useLocation();
  const videoRef = useRef(null);
  const panelBodyRef = useRef(null);
  const playerWrapRef = useRef(null);
  const progressWrapRef = useRef(null);
  const progressFillRef = useRef(null);
  const progressThumbRef = useRef(null);
  const timecodeRef = useRef(null);
  const annPopupRef = useRef(null);
  const popupLayoutRef = useRef([]);
  const wasDraggingRef = useRef(false);
  const seekingRef = useRef(false);
  const rafRef = useRef(null);
  const playingRef = useRef(false);
  const wasPlayingBeforeSeekRef = useRef(false);
  const lastScrolledFeedbackRef = useRef(null);

  const [video, setVideo] = useState(null);
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [status, setStatus] = useState('none');
  const [tab, setTab] = useState('feedback');

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [loop, setLoop] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [newFbIds, setNewFbIds] = useState([]);
  const [popupFeedback, setPopupFeedback] = useState(null);

  const [dragBox, setDragBox] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [annPopup, setAnnPopup] = useState(null);
  const [annText, setAnnText] = useState('');

  const [commentOpen, setCommentOpen] = useState({});
  const [commentText, setCommentText] = useState({});

  const [sharePanel, setSharePanel] = useState(false);
  const [uploadPanel, setUploadPanel] = useState(false);
  const [verPopup, setVerPopup] = useState(false);
  const [statusPopup, setStatusPopup] = useState(false);
  const [morePopup, setMorePopup] = useState(false);
  const [volPopup, setVolPopup] = useState(false);
  const [settingsPopup, setSettingsPopup] = useState(false);

  const [sharePw, setSharePw] = useState('');
  const [sharePw2, setSharePw2] = useState('');
  const [shareErr, setShareErr] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [shareStep, setShareStep] = useState(1);
  const [shareCopied, setShareCopied] = useState(false);

  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const fileInputRef = useRef(null);
  const ALLOWED_EXT = ['mp4'];
  const MAX_SIZE = 4 * 1024 * 1024 * 1024;

  const [deleteTarget, setDeleteTarget] = useState(null);

  const navState = location.state || {};

  useEffect(() => { fetchAll(); }, [videoId]);

  // 피드백 자동 갱신 (3초)
  useEffect(() => {
    if (!currentVersion) return;
    const t = setInterval(async () => {
      try {
        const res = await axios.get(`/api/feedbacks/index.php?video_version_id=${currentVersion.id}`, { withCredentials: true });
        setFeedbacks(res.data);
        const arrived = res.data.filter(f => Number(f.is_new) === 1).map(f => f.id);
        if (arrived.length > 0) {
          setNewFbIds(prev => [...new Set([...prev, ...arrived])]);
          axios.put('/api/feedbacks/index.php', { video_version_id: currentVersion.id }, { withCredentials: true }).catch(() => {});
        }
      } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [currentVersion]);

  // 버전 목록 자동 갱신 (60초)
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const res = await axios.get(`/api/videoviewer/versions/index.php?video_id=${videoId}`, { withCredentials: true });
        setVersions(res.data);
      } catch {}
    }, 60000);
    return () => clearInterval(t);
  }, [videoId]);

  const fetchAll = async () => {
    try {
      const [versRes, statusRes] = await Promise.all([
        axios.get(`/api/videoviewer/versions/index.php?video_id=${videoId}`, { withCredentials: true }),
        axios.get(`/api/videoviewer/status/index.php?video_id=${videoId}`, { withCredentials: true }),
      ]);
      setVersions(versRes.data);
      setStatus(statusRes.data.status || 'none');
      setVideo(statusRes.data);
      if (versRes.data.length > 0) {
        setCurrentVersion(versRes.data[0]);
        await fetchFeedbacks(versRes.data[0].id);
      }
      // 입장 시 모든 버전의 새 피드백 읽음 처리 (목록을 받아온 뒤 실행)
      axios.put('/api/feedbacks/index.php', { video_id: Number(videoId) }, { withCredentials: true }).catch(() => {});
    } catch (e) {
      if (e?.response?.status === 401) navigate('/login');
    }
  };

  const fetchFeedbacks = async (versionId) => {
    try {
      const res = await axios.get(`/api/feedbacks/index.php?video_version_id=${versionId}`, { withCredentials: true });
      setFeedbacks(res.data);
      const arrived = res.data.filter(f => Number(f.is_new) === 1).map(f => f.id);
      if (arrived.length > 0) setNewFbIds(prev => [...new Set([...prev, ...arrived])]);
      axios.put('/api/feedbacks/index.php', { video_version_id: versionId }, { withCredentials: true }).catch(() => {});
      return res.data;
    } catch {}
  };

  const handleTimeUpdate = useCallback(() => {
    const t = videoRef.current?.currentTime || 0;
    setCurrentTime(t);
    const cur = feedbacks.find(f => Math.abs(fromTimecode(f.timecode) - t) < 0.5);
    if (cur) {
      setSelectedFeedback(cur.id);
      if (lastScrolledFeedbackRef.current !== cur.id) {
        lastScrolledFeedbackRef.current = cur.id;
        const el = panelBodyRef.current?.querySelector(`[data-id="${cur.id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [feedbacks]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const tick = () => {
      if (!v) return;
      const t = v.currentTime;
      const dur = v.duration || 0;
      if (!seekingRef.current) {
        if (progressFillRef.current) progressFillRef.current.style.width = dur ? `${(t / dur) * 100}%` : '0%';
        if (progressThumbRef.current) progressThumbRef.current.style.left = dur ? `${(t / dur) * 100}%` : '0%';
        if (timecodeRef.current) timecodeRef.current.textContent = toTimecode(t);
        setCurrentTime(t);
      }
      handleTimeUpdate();
      rafRef.current = requestAnimationFrame(tick);
    };

    const onMeta = () => setDuration(v.duration);
    const onPlay = () => { setPlaying(true); playingRef.current = true; setPopupFeedback(null); rafRef.current = requestAnimationFrame(tick); };
    const onPause = () => { setPlaying(false); playingRef.current = false; cancelAnimationFrame(rafRef.current); };
    const onEnded = () => { setPlaying(false); playingRef.current = false; cancelAnimationFrame(rafRef.current); };

    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('ended', onEnded);
    if (!v.paused && !v.ended) rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('ended', onEnded);
    };
  }, [handleTimeUpdate, currentVersion]);

  const togglePlay = () => {
    if (wasDraggingRef.current) return;
    const v = videoRef.current;
    if (!v) return;
    if (playing) { v.pause(); setPlaying(false); } else { v.play(); setPlaying(true); }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code !== 'Space') return;
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      e.preventDefault();
      togglePlay();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [playing]);

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    if (videoRef.current) {
      videoRef.current.currentTime = ratio * duration;
      if (wasPlayingBeforeSeekRef.current) videoRef.current.play();
    }
    setCurrentTime(ratio * duration);
  };

  const handleProgressMouseDown = (e) => {
    seekingRef.current = true;
    wasPlayingBeforeSeekRef.current = playingRef.current;
    if (videoRef.current) { videoRef.current.pause(); }
    setPlaying(false);
    const seek = (clientX) => {
      const r = progressWrapRef.current.getBoundingClientRect();
      const rt = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
      const t = rt * (videoRef.current?.duration || 0);
      if (videoRef.current) videoRef.current.currentTime = t;
      if (progressFillRef.current) progressFillRef.current.style.width = `${rt * 100}%`;
      if (progressThumbRef.current) progressThumbRef.current.style.left = `${rt * 100}%`;
      if (timecodeRef.current) timecodeRef.current.textContent = toTimecode(t);
    };
    seek(e.clientX);
    const onMove = (ev) => { if (seekingRef.current) seek(ev.clientX); };
    const onUp = () => {
      seekingRef.current = false;
      setCurrentTime(videoRef.current?.currentTime || 0);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('[data-annpopup]')) return;
    const rect = playerWrapRef.current.getBoundingClientRect();
    setDragStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setIsDragging(false);
    setAnnPopup(null);
    setDragBox(null);
  };
  const handleMouseMove = (e) => {
    if (!dragStart) return;
    const rect = playerWrapRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    if (Math.abs(cx - dragStart.x) > 5 || isDragging) {
      setIsDragging(true);
      setDragBox({ left: Math.min(dragStart.x, cx), top: Math.min(dragStart.y, cy), width: Math.abs(cx - dragStart.x), height: Math.abs(cy - dragStart.y) });
    }
  };
  const handleMouseUp = (e) => {
    if (!dragStart) return;
    const rect = playerWrapRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const w = Math.abs(cx - dragStart.x);
    const h = Math.abs(cy - dragStart.y);
    if (isDragging && w > 10 && h > 10) {
      wasDraggingRef.current = true;
      setTimeout(() => { wasDraggingRef.current = false; }, 100);
      videoRef.current?.pause();
      setPlaying(false);
      const box = { left: Math.min(dragStart.x, cx), top: Math.min(dragStart.y, cy), width: w, height: h, rectW: rect.width, rectH: rect.height };
      setDragBox(box);
      const popW = 286;
      let px = box.left + w + 8;
      if (px + popW > rect.width) px = box.left - popW - 8;
      if (px < 0) px = 0;
      let py = box.top;
      if (py + 180 > rect.height) py = rect.height - 180;
      if (py < 0) py = 0;
      setAnnPopup({ left: px, top: py });
      setAnnText('');
    }
    setDragStart(null);
    setIsDragging(false);
  };

  const cancelAnn = () => { setAnnPopup(null); setDragBox(null); };

  const truncate = (str, n = 20) => str && str.length > n ? str.slice(0, n) + '...' : str;

  const autoGrow = (e) => {
    if (e.target.scrollHeight > e.target.offsetHeight) {
      e.target.style.height = e.target.scrollHeight + 'px';
      repositionAnnPopup();
    }
  };

  const repositionAnnPopup = () => {
    const popup = annPopupRef.current;
    const wrap = playerWrapRef.current;
    if (!popup || !wrap) return;
    const wrapRect = wrap.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();
    const overflowBottom = popupRect.bottom - wrapRect.bottom;
    if (overflowBottom > 0) {
      const currentTop = parseFloat(popup.style.top) || 0;
      popup.style.top = Math.max(0, currentTop - overflowBottom) + 'px';
    }
  };

  const handleSubmitFeedback = async () => {
    if (!annText.trim() || !currentVersion) return;
    const rect = playerWrapRef.current?.getBoundingClientRect();
    const near = feedbacks.find(f => Math.abs(fromTimecode(f.timecode) - currentTime) <= (5 / 30));
    const tc = near ? near.timecode : toTimecode(currentTime);
    await axios.post('/api/feedbacks/index.php', {
      video_version_id: currentVersion.id,
      content: annText,
      timecode: tc,
      area_x: dragBox && rect ? dragBox.left / rect.width : null,
      area_y: dragBox && rect ? dragBox.top / rect.height : null,
      area_width: dragBox && rect ? dragBox.width / rect.width : null,
      area_height: dragBox && rect ? dragBox.height / rect.height : null,
    }, { withCredentials: true });
    const savedTime = currentTime;
    const savedText = annText;
    cancelAnn();
    const newList = await fetchFeedbacks(currentVersion.id);
    if (videoRef.current) videoRef.current.currentTime = savedTime;
    setCurrentTime(savedTime);
    const matched = newList?.find(f => f.timecode === tc && f.content === savedText);
    if (matched) setPopupFeedback(matched.id);
  };

  const handleDeleteVersion = async () => {
    await axios.delete('/api/videoviewer/versions/index.php', { data: { id: deleteTarget.id }, withCredentials: true });
    setDeleteTarget(null);
    fetchAll();
  };

  const handleDeleteFeedback = async () => {
    await axios.delete('/api/feedbacks/index.php', { data: { id: deleteTarget.id }, withCredentials: true });
    setDeleteTarget(null); fetchFeedbacks(currentVersion.id);
  };
  const handleDeleteComment = async () => {
    await axios.delete('/api/feedbacks/comments/index.php', { data: { id: deleteTarget.id }, withCredentials: true });
    setDeleteTarget(null); fetchFeedbacks(currentVersion.id);
  };

  const handleAddComment = async (feedbackId) => {
    const text = commentText[feedbackId];
    if (!text?.trim()) return;
    await axios.post('/api/feedbacks/comments/index.php', { feedback_id: feedbackId, content: text }, { withCredentials: true });
    setCommentText(p => ({ ...p, [feedbackId]: '' }));
    setCommentOpen(p => ({ ...p, [feedbackId]: false }));
    fetchFeedbacks(currentVersion.id);
  };

  const handleStatusChange = async (s) => {
    await axios.put('/api/videoviewer/status/index.php', { video_id: Number(videoId), status: s }, { withCredentials: true });
    setStatus(s); setStatusPopup(false);
  };

  const handleVersionSelect = (v) => { setCurrentVersion(v); fetchFeedbacks(v.id); setVerPopup(false); };

  const addUploadFiles = (files) => {
    const list = Array.from(files);
    const invalid = list.filter(f => !ALLOWED_EXT.includes(f.name.split('.').pop().toLowerCase()) || f.size > MAX_SIZE);
    const valid = list.filter(f => ALLOWED_EXT.includes(f.name.split('.').pop().toLowerCase()) && f.size <= MAX_SIZE);
    if (invalid.length > 0) {
      alert(`다음 파일은 추가할 수 없습니다:\n${invalid.map(f => {
        const ext = f.name.split('.').pop().toLowerCase();
        return !ALLOWED_EXT.includes(ext) ? `${f.name} (지원하지 않는 형식)` : `${f.name} (4GB 초과)`;
      }).join('\n')}`);
    }
    if (!valid.length) return;
    setUploadFiles(prev => {
      const merged = [...prev];
      valid.forEach(f => { if (!merged.find(x => x.file.name === f.name && x.file.size === f.size)) merged.push({ file: f, progress: 0, status: 'pending' }); });
      return merged;
    });
  };

  const handleUpload = async () => {
    if (!uploadFiles.length || uploading) return;
    setUploading(true);
    for (let i = 0; i < uploadFiles.length; i++) {
      const item = uploadFiles[i];
      if (item.status === 'done') continue;
      const formData = new FormData();
      formData.append('file', item.file);
      formData.append('video_id', videoId);
      try {
        await axios.post('/api/videoviewer/upload/index.php', formData, {
          withCredentials: true,
          onUploadProgress: e => { if (e.total) { const pct = Math.round(e.loaded / e.total * 100); setUploadFiles(prev => prev.map((x, j) => j === i ? { ...x, progress: pct } : x)); } },
        });
        setUploadFiles(prev => prev.map((x, j) => j === i ? { ...x, progress: 100, status: 'done' } : x));
      } catch {
        setUploadFiles(prev => prev.map((x, j) => j === i ? { ...x, status: 'error' } : x));
      }
    }
    setUploading(false);
    setAllDone(true);
    fetchAll();
  };

  const handleCreateShare = async () => {
    if (sharePw && sharePw !== sharePw2) { setShareErr('비밀번호가 일치하지 않습니다'); return; }
    try {
      const res = await axios.post('/api/sharelinks/index.php', { type: 'video', target_id: Number(videoId), password: sharePw }, { withCredentials: true });
      setShareUrl(res.data.url); setShareStep(2);
    } catch { setShareErr('링크 생성에 실패했습니다'); }
  };

  const jumpToFeedback = (f) => {
    setSelectedFeedback(f.id);
    setPopupFeedback(f.id);
    const t = fromTimecode(f.timecode);
    const dur = videoRef.current?.duration || 0;
    if (videoRef.current) {
      videoRef.current.pause();
      setPlaying(false);
      videoRef.current.currentTime = t;
    }
    setCurrentTime(t);
    if (dur) {
      const pct = `${(t / dur) * 100}%`;
      if (progressFillRef.current) progressFillRef.current.style.width = pct;
      if (progressThumbRef.current) progressThumbRef.current.style.left = pct;
    }
    if (timecodeRef.current) timecodeRef.current.textContent = toTimecode(t);
    lastScrolledFeedbackRef.current = f.id;
    const el = panelBodyRef.current?.querySelector(`[data-id="${f.id}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const prevFeedback = () => {
    if (!feedbacks.length) return;
    const sorted = [...feedbacks].sort((a, b) => fromTimecode(a.timecode) - fromTimecode(b.timecode));
    const t = videoRef.current?.currentTime ?? currentTime;
    let prev = null;
    for (let i = sorted.length - 1; i >= 0; i--) {
      if (fromTimecode(sorted[i].timecode) < t - 0.05) { prev = sorted[i]; break; }
    }
    jumpToFeedback(prev || sorted[sorted.length - 1]);
  };
  const nextFeedback = () => {
    if (!feedbacks.length) return;
    const sorted = [...feedbacks].sort((a, b) => fromTimecode(a.timecode) - fromTimecode(b.timecode));
    const t = videoRef.current?.currentTime ?? currentTime;
    const next = sorted.find(f => fromTimecode(f.timecode) > t + 0.05);
    jumpToFeedback(next || sorted[0]);
  };

  const closeAllPopups = () => {
    setVerPopup(false); setStatusPopup(false); setMorePopup(false);
    setVolPopup(false); setSettingsPopup(false);
  };

  const progressRatio = duration ? currentTime / duration : 0;
  const st = STATUS_MAP[status] || STATUS_MAP.none;
  const dotColor = DOT_COLORS[st.cls];
  const managerName = video?.manager_name || navState.managerName || '';
  const projectName = video?.project_name || navState.projectName || '';
  const videoName = video?.name || video?.video_name || navState.videoName || '';
  const videoNameClean = videoName.replace(/\.(mp4|mov|avi|mkv|wmv|webm)$/i, '');
  const videoNameDisplay = videoNameClean.length > 25 ? videoNameClean.slice(0, 25) + '...' : videoNameClean;

  return (
    <div style={s.wrap} onClick={closeAllPopups}>
      <style>{`
        .fb-item .fb-del-btn, .comment-item .comment-del-btn { opacity: 0; transition: opacity .15s, color .15s; }
        .fb-item:hover .fb-del-btn, .comment-item:hover .comment-del-btn { opacity: 1; }
        .fb-del-btn:hover, .comment-del-btn:hover { color: #fff; }
        .del-tooltip { position: absolute; top: calc(100% + 12px); left: 50%; transform: translateX(-50%); background: #fff; color: #1a1a1a; font-size: 11pt; padding: 3px 8px; border-radius: 5px; white-space: nowrap; pointer-events: none; opacity: 0; transition: opacity .15s; z-index: 10; }
        .fb-del-btn:hover .del-tooltip, .comment-del-btn:hover .del-tooltip { opacity: 1; }
        .ver-row:hover { background: rgba(255,255,255,0.08) !important; }
        input[type=range].vol-slider::-webkit-slider-runnable-track { border-radius: 4px; }
        input[type=range].vol-slider::-moz-range-track { border-radius: 4px; }
        input[type=range].vol-slider::-webkit-slider-thumb { border-radius: 50%; }
        input[type=range].vol-slider::-moz-range-thumb { border-radius: 50%; }
        .prog-thumb { transition: transform .15s; }
        .prog-wrap:hover .prog-track { height: 7px !important; }
        .prog-wrap:hover .prog-thumb { transform: translate(-50%,-50%) scale(1.4) !important; }
      `}</style>
      {/* 상단 바 */}
      <div style={s.topbar}>
        <LogoSVG size={32} style={{ flexShrink: 0, marginRight: 4 }} />
        <div style={s.divider} />
        <div style={s.topbarPath}>
          <span style={s.topbarWs} onClick={e => { e.stopPropagation(); navigate('/'); }}>WORKSPACE</span>
          <span style={s.topbarArrow}><ArrowRight /></span>
          {managerName && <>
            <span style={{ ...s.topbarWs, display: 'inline-flex', alignItems: 'center', gap: 5 }} onClick={e => { e.stopPropagation(); navigate(-2); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
              {managerName}
            </span>
            <span style={s.topbarArrow}><ArrowRight /></span>
          </>}
          {projectName && <>
            <span style={{ ...s.topbarWs, display: 'inline-flex', alignItems: 'center', gap: 5 }} onClick={e => { e.stopPropagation(); navigate(-1); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
              {projectName}
            </span>
            <span style={s.topbarArrow}><ArrowRight /></span>
          </>}
          <span style={s.topbarCurrent} title={videoName}>{videoNameDisplay}</span>
          <div style={{ position: 'relative', marginLeft: 2 }} onClick={e => e.stopPropagation()}>
            <button style={s.verBtn} onClick={() => { closeAllPopups(); setVerPopup(p => !p); }}>
              {currentVersion ? `V${currentVersion.version_number}` : '버전'}
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            {verPopup && (
              <div style={s.popup}>
                {versions.map((v, i) => (
                  <div key={v.id}>
                    {i > 0 && <div style={s.popDivider} />}
                    <div
                      className="ver-row"
                      style={s.verRow}
                      onClick={() => handleVersionSelect(v)}>
                      <div style={s.verThumb}>
                        {v.thumbnail_path
                          ? <img src={v.thumbnail_path} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />
                          : '▶'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={s.verNum}>V{v.version_number}{i === 0 && <span style={s.verLatestTag}> 최신</span>}</div>
                        <div style={s.verFile}>{truncate(v.original_name ? `v${v.version_number}_${v.original_name.replace(/\.[^.]+$/, '')}` : `v${v.version_number}_${videoNameClean}`)}</div>
                        <div style={s.verDate}>{v.uploaded_at?.slice(0,10).replace(/-/g,'/')}</div>
                      </div>
                      <button style={s.verDel} onClick={e => { e.stopPropagation(); setDeleteTarget({ id: v.id, type: 'version' }); }}>{TRASH_SVG}</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={s.topbarRight} onClick={e => e.stopPropagation()}>
          <button style={s.compareBtn} onClick={() => navigate(`/compare/${videoId}`, { state: { videoName, managerName, projectName } })}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 6H3"/><polyline points="7 2 3 6 7 10"/><path d="M3 18h18"/><polyline points="17 14 21 18 17 22"/>
            </svg>
            버전 비교
          </button>

          <div style={{ position: 'relative' }}>
            <button style={s.statusBtn} onClick={() => { closeAllPopups(); setStatusPopup(p => !p); }}>
              <span style={{ ...s.sDot, background: dotColor }} />
              <span>{st.label}</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            {statusPopup && (
              <div style={{ ...s.popup, ...s.popupRight }}>
                {Object.entries(STATUS_MAP).map(([key, val]) => (
                  <div key={key} style={s.statusItem} onClick={() => handleStatusChange(key)}>
                    <span style={{ ...s.sDot, background: DOT_COLORS[val.cls] }} />{val.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button style={s.compareBtn} onClick={() => currentVersion && window.open(`/api/download/index.php?version_id=${currentVersion.id}`, '_blank')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="8 17 12 21 16 17"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29"/>
            </svg>
            다운로드
          </button>

          <div style={{ position: 'relative' }}>
            <button style={s.moreBtn} onClick={() => { closeAllPopups(); setMorePopup(p => !p); }}>⋯</button>
            {morePopup && (
              <div style={{ ...s.popup, ...s.popupRight }}>
                <div style={s.moreItem} onClick={() => { setMorePopup(false); setSharePanel(true); setShareStep(1); setSharePw(''); setSharePw2(''); setShareErr(''); setShareUrl(''); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>공유
                </div>
                <div style={s.moreItem} onClick={() => { setMorePopup(false); setUploadPanel(true); setUploadFiles([]); setAllDone(false); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>버전 업데이트
                </div>
                <div style={s.moreItem}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>이름 변경
                </div>
                <div style={s.moreItem} onClick={() => currentVersion && window.open(`/api/download/index.php?version_id=${currentVersion.id}`, '_blank')}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="8 17 12 21 16 17"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29"/></svg>다운로드
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 메인 */}
      <div style={s.main}>
        {/* 영상 컬럼 */}
        <div style={s.videoCol}>
          <div style={s.playerArea}>
            <div ref={playerWrapRef} style={s.playerWrap}
              onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
              {currentVersion?.file_path ? (
                <video ref={videoRef} src={currentVersion.file_path} style={s.video} loop={loop} />
              ) : (
                <div style={s.playerInner}>
                  <div style={s.playCircle} onClick={togglePlay}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                  </div>
                </div>
              )}

              {(popupLayoutRef.current = []) && null}
              {feedbacks.filter(f => f.area_x != null).map((f, idx) => {
                const rect = playerWrapRef.current?.getBoundingClientRect();
                if (!rect) return null;
                const fTime = fromTimecode(f.timecode);
                const visible = Math.abs(currentTime - fTime) <= (5 / 30);
                if (!visible) return null;
                const boxLeft = f.area_x * rect.width;
                const boxTop = f.area_y * rect.height;
                const boxW = f.area_width * rect.width;
                const popW = 260;
                let px = boxLeft + boxW + 8;
                if (px + popW > rect.width) px = boxLeft - popW - 8;
                if (px < 0) px = 0;
                let py = boxTop;
                if (py + 120 > rect.height) py = rect.height - 120;
                if (py < 0) py = 0;
                return (
                  <div key={f.id}>
                    <div style={{ position: 'absolute', left: boxLeft, top: boxTop, width: boxW, height: f.area_height * rect.height, border: '2px solid #ffbc00', borderRadius: 3, pointerEvents: 'auto', opacity: selectedFeedback === f.id ? 1 : 0.6, cursor: 'pointer' }}
                      onClick={e => { e.stopPropagation(); setSelectedFeedback(f.id); setPopupFeedback(p => p === f.id ? null : f.id); }}>
                      <div style={{ position: 'absolute', top: -20, left: -20, width: 40, height: 40, borderRadius: '50%', background: '#ffbc00', color: '#fff', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{idx + 1}</div>
                    </div>
                    {popupFeedback != null && (() => { const p = feedbacks.find(x => x.id === popupFeedback); return p && Math.abs(fromTimecode(p.timecode) - fTime) <= (5 / 30); })() && (
                      <div
                        style={{ ...s.annPopup, left: px, top: py, width: popW, zIndex: popupFeedback === f.id ? 20 : 10 }}
                        onClick={e => e.stopPropagation()}
                        ref={el => {
                          if (!el || !rect) return;
                          const elH = el.offsetHeight;
                          let newTop = py;
                          const others = popupLayoutRef.current.filter(r => r.id !== f.id && r.left < px + popW && r.left + popW > px);
                          let moved = true;
                          while (moved) {
                            moved = false;
                            for (const r of others) {
                              if (newTop < r.bottom + 8 && newTop + elH > r.top - 8) { newTop = r.bottom + 8; moved = true; }
                            }
                          }
                          if (newTop + elH > rect.height) newTop = Math.max(0, rect.height - elH);
                          if (Math.abs(parseFloat(el.style.top) - newTop) > 0.5) el.style.top = newTop + 'px';
                          const entry = { id: f.id, top: newTop, bottom: newTop + elH, left: px };
                          const i = popupLayoutRef.current.findIndex(r => r.id === f.id);
                          if (i >= 0) popupLayoutRef.current[i] = entry; else popupLayoutRef.current.push(entry);
                        }}
                      >
                        <div style={s.annHeader}>
                          <span style={s.annName}>{f.author_name || '관리자'}</span>
                          <span style={s.annDate}>{f.created_at?.slice(0,16).replace(/-/g,'/')}</span>
                        </div>
                        <div style={{ fontSize: '12pt', color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, marginTop: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{f.content}</div>
                        {f.comments?.length > 0 && (
                          <div style={s.commentList}>
                            {f.comments.map(c => (
                              <div key={c.id} style={s.commentItem}>
                                <div style={s.commentHeader}>
                                  <span style={s.commentAuthor}>{c.author_name}</span>
                                  <span style={s.commentDate}>{c.created_at?.slice(0,16).replace(/-/g,'/')}</span>
                                </div>
                                <div style={s.commentTxt}>{c.content}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{ marginTop: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '6px 10px' }}>
                          <textarea
                            style={{ ...s.commentInput, background: 'transparent', border: 'none', padding: 0, resize: 'none', minHeight: 14 }}
                            placeholder="댓글을 입력하세요..."
                            value={commentText[f.id] || ''}
                            onChange={e => setCommentText(p => ({ ...p, [f.id]: e.target.value }))}
                            onInput={autoGrow}
                            ref={el => { if (el && !commentText[f.id]) el.style.height = 'auto'; }}
                            rows={1}
                            onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleAddComment(f.id); }}
                          />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                          <button style={{ ...s.commentBtn, ...s.commentBtnPrimary }} onClick={() => handleAddComment(f.id)}>저장</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {dragBox && (
                <div style={{ position: 'absolute', left: dragBox.left, top: dragBox.top, width: dragBox.width, height: dragBox.height, border: '2px solid #ffbc00', borderRadius: 3, pointerEvents: 'none' }}>
                  <div style={{ position: 'absolute', top: -20, left: -20, width: 40, height: 40, borderRadius: '50%', background: '#ffbc00', color: '#fff', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{feedbacks.length + 1}</div>
                </div>
              )}

              {annPopup && (
                <div data-annpopup="1" ref={el => { annPopupRef.current = el; if (el) setTimeout(repositionAnnPopup, 0); }} style={{ ...s.annPopup, left: annPopup.left, top: annPopup.top }}>
                  <div style={s.annHeader}>
                    <span style={s.annName}>관리자</span>
                    <span style={s.annDate}>{(d => `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`)(new Date())}</span>
                  </div>
                  <textarea style={s.annTextarea} placeholder="이 영역에 대한 피드백을 입력하세요..." value={annText} onChange={e => setAnnText(e.target.value)} onInput={autoGrow} autoFocus />
                  <div style={s.annActions}>
                    <button style={s.annBtn} onClick={cancelAnn}>취소</button>
                    <button style={{ ...s.annBtn, ...s.annBtnPrimary }} onClick={handleSubmitFeedback}>등록</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 컨트롤 */}
          <div style={s.controls}>
            <div ref={progressWrapRef} className="prog-wrap" style={s.progressWrap} onClick={handleSeek} onMouseDown={handleProgressMouseDown}>
              <div className="prog-track" style={s.progressTrack}>
                <div ref={progressFillRef} style={{ height: '100%', width: `${progressRatio * 100}%`, background: '#fff', borderRadius: 2 }} />
              </div>
              <div ref={progressThumbRef} className="prog-thumb" style={{ position: 'absolute', top: '50%', left: `${progressRatio * 100}%`, transform: 'translate(-50%,-50%)', width: 13, height: 13, borderRadius: '50%', background: '#fff', pointerEvents: 'none' }} />
            </div>
            <div style={s.markerRow}>
              {duration > 0 && feedbacks.map(f => (
                <div key={f.id} style={{ ...s.fbMarker, left: `${(fromTimecode(f.timecode) / duration) * 100}%` }} onClick={() => jumpToFeedback(f)} />
              ))}
            </div>
            <div style={s.ctrlRow}>
              <div style={s.ctrlSide} />
              <div style={s.ctrlCenter}>
                <button style={s.ctrlBtn} onClick={prevFeedback}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <button style={s.ctrlBtn} onClick={nextFeedback}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
                <button style={s.ctrlBtn} onClick={togglePlay}>
                  {playing
                    ? <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="2"/><rect x="14" y="4" width="4" height="16" rx="2"/></svg>
                    : <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M7 4.5C7 3.4 8.2 2.7 9.1 3.3l11.5 7.5c0.8 0.5 0.8 1.7 0 2.2L9.1 20.7C8.2 21.3 7 20.6 7 19.5V4.5z"/></svg>
                  }
                </button>
                <span style={s.timecode}>
                  <span ref={timecodeRef} style={{ color: '#fff' }}>{toTimecode(currentTime)}</span>
                  <span style={{ color: 'rgba(255,255,255,0.15)', margin: '0 3px' }}>/</span>
                  {toTimecode(duration)}
                </span>
              </div>
              <div style={{ ...s.ctrlSide, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                <div style={{ position: 'relative' }}>
                  <button style={s.ctrlBtn} onClick={() => { closeAllPopups(); setVolPopup(p => !p); }}>
                    {volume === 0
                      ? <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                      : <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                    }
                  </button>
                  {volPopup && (
                    <div style={s.volPopup}>
                      <span style={{ fontSize: 12.8, color: 'rgba(255,255,255,0.4)' }}>{Math.round(volume * 100)}%</span>
                      <input type="range" min={0} max={1} step={0.01} value={volume} style={s.volSlider} className="vol-slider"
                        onChange={e => { const v = Number(e.target.value); setVolume(v); if (videoRef.current) videoRef.current.volume = v; }} />
                    </div>
                  )}
                </div>
                <button style={{ ...s.ctrlBtn, color: loop ? '#5755df' : s.ctrlBtn.color }} onClick={() => setLoop(l => !l)} title="반복">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                  </svg>
                </button>
                <button style={s.ctrlBtn} onClick={() => videoRef.current?.requestFullscreen()} title="전체화면">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
                  </svg>
                </button>
                <div style={{ position: 'relative' }}>
                  <button style={s.ctrlBtn} onClick={() => { closeAllPopups(); setSettingsPopup(p => !p); }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                    </svg>
                  </button>
                  {settingsPopup && (
                    <div style={s.settingsPopup}>
                      <div style={s.setLabel}>재생 속도</div>
                      {[0.5, 0.75, 1, 1.5, 2, 4].map(r => (
                        <div key={r} style={{ ...s.setItem, ...(playbackRate === r ? { color: '#fff', fontWeight: 700, background: 'rgba(165,163,255,0.18)' } : {}) }}
                          onClick={() => { setPlaybackRate(r); if (videoRef.current) videoRef.current.playbackRate = r; }}>
                          {r}x {playbackRate === r && <span style={{ color: '#a5a3ff' }}>✓</span>}
                        </div>
                      ))}
                      <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', margin: '3px 0' }} />
                      <div style={s.setLabel}>화질</div>
                      {['1080p','720p','480p'].map(q => (
                        <div key={q} style={s.setItem}>{q}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 우측 패널 */}
        <div style={s.sidePanel}>
          {/* 공유 패널 */}
          <div style={{ ...s.slidePanel, transform: sharePanel ? 'translateX(0)' : 'translateX(100%)' }}>
            <div style={s.slidePanelHeader}>
              <span style={s.slidePanelTitle}>공유 링크 생성</span>
              <button style={s.slideClose} onClick={() => setSharePanel(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={s.slideBody}>
              {shareStep === 1 ? (<>
                <div style={s.shareField}><label style={s.shareLabel}>비밀번호</label><input style={s.shareInput} type="password" placeholder="공유 비밀번호" value={sharePw} onChange={e => setSharePw(e.target.value)} /></div>
                <div style={s.shareField}><label style={s.shareLabel}>비밀번호 확인</label><input style={s.shareInput} type="password" placeholder="비밀번호 재입력" value={sharePw2} onChange={e => setSharePw2(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreateShare()} /></div>
                {shareErr && <div style={{ color: '#ff6b6b', fontSize: 10 }}>{shareErr}</div>}
                <div style={s.shareActions}>
                  <button style={s.shareBtn} onClick={() => setSharePanel(false)}>취소</button>
                  <button style={{ ...s.shareBtn, ...s.shareBtnPrimary }} onClick={handleCreateShare}>링크 생성</button>
                </div>
              </>) : (<>
                <div style={s.shareField}><label style={s.shareLabel}>생성된 링크</label><div style={s.shareUrlBox}>{shareUrl}</div></div>
                <div style={s.shareActions}>
                  <button style={s.shareBtn} onClick={() => setSharePanel(false)}>닫기</button>
                  <button
                    style={{ ...s.shareBtn, ...s.shareBtnPrimary, minWidth: 86, textAlign: 'center', ...(shareCopied ? { background: '#8bc34a', borderColor: '#8bc34a' } : {}) }}
                    onClick={() => { navigator.clipboard.writeText(shareUrl); setShareCopied(true); setTimeout(() => setShareCopied(false), 1500); }}
                  >{shareCopied ? '복사 완료' : '링크 복사'}</button>
                  <button
                    style={{ ...s.shareBtn, background: '#FEE500', border: 'none', padding: '0 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    onClick={() => {
                      if (!window.Kakao) return;
                      if (!window.Kakao.isInitialized()) window.Kakao.init('1c487e4eaa7b1b677c3f3f5f2bb70163');
                      const token = new URL(shareUrl).searchParams.get('token');
                      const thumb = currentVersion?.thumbnail_path
                        ? 'https://jerry2126.vommedia.co.kr' + currentVersion.thumbnail_path
                        : 'https://jerry2126.vommedia.co.kr/vommedia2.jpg';
                      window.Kakao.Share.sendCustom({
                        templateId: 134953,
                        templateArgs: { TOKEN: token, THU: thumb, TITLE: videoName || '', PW: sharePw || '없음' },
                      });
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 4C11.163 4 4 9.82 4 17c0 4.754 3.07 8.926 7.698 11.345L10.1 34.77a.5.5 0 0 0 .718.543L18.2 30.8c.595.06 1.196.1 1.8.1 8.837 0 16-5.82 16-13S28.837 4 20 4z" fill="#3C1E1E"/>
                    </svg>
                  </button>
                </div>
              </>)}
            </div>
          </div>

          {/* 업로드 패널 */}
          <div style={{ ...s.slidePanel, transform: uploadPanel ? 'translateX(0)' : 'translateX(100%)' }}>
            <div style={s.slidePanelHeader}>
              <span style={s.slidePanelTitle}>버전 업데이트</span>
              <button style={s.slideClose} onClick={() => setUploadPanel(false)}>
                <svg width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={s.slideBody}>
              <div style={s.dropZone}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); addUploadFiles(e.dataTransfer.files); }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                  <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                </svg>
                <span style={s.dropLabel}>파일을 여기에 드래그하거나<br/>클릭하여 선택하세요</span>
                <span style={s.dropSub}>MP4 형식 · 최대 4GB · 여러 파일 동시 선택 가능</span>
              </div>
              <input ref={fileInputRef} type="file" accept="video/*" multiple style={{ display: 'none' }}
                onChange={e => addUploadFiles(e.target.files)} />

              {uploadFiles.length > 0 && (
                <div style={s.fileList}>
                  {uploadFiles.map((item, i) => (
                    <div key={i} style={s.fileRow}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.6" strokeLinecap="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                      </svg>
                      <div style={s.fileInfo}>
                        <div style={s.fileName}>{item.file.name}</div>
                        <div style={s.fileSize}>{(item.file.size / 1024 / 1024).toFixed(1)} MB</div>
                        <div style={s.progressBarBg}>
                          <div style={{ ...s.progressBarFill, width: item.progress + '%', background: item.status === 'done' ? '#28c76f' : item.status === 'error' ? '#e74c3c' : '#5755df' }} />
                        </div>
                      </div>
                      {!uploading && item.status !== 'done' && (
                        <button style={s.fileRemove} onClick={() => setUploadFiles(prev => prev.filter((_, j) => j !== i))}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div style={s.uploadActions}>
                <button style={s.uploadBtn} onClick={() => setUploadPanel(false)} disabled={uploading}>취소</button>
                {allDone
                  ? <button style={{ ...s.uploadBtn, ...s.uploadBtnPrimary }} onClick={() => setUploadPanel(false)}>완료</button>
                  : <button style={{ ...s.uploadBtn, ...s.uploadBtnPrimary }} onClick={handleUpload} disabled={uploading || uploadFiles.length === 0}>업로드</button>
                }
              </div>
            </div>
          </div>

          {/* 탭 */}
          <div style={s.panelTabs}>
            <div style={{ ...s.panelTab, color: tab === 'feedback' ? '#fff' : 'rgba(255,255,255,0.3)' }} onClick={() => setTab('feedback')}>피드백</div>
            <div style={{ ...s.panelTab, color: tab === 'version' ? '#fff' : 'rgba(255,255,255,0.3)' }} onClick={() => setTab('version')}>버전</div>
            <div style={{ ...s.tabIndicator, transform: tab === 'version' ? 'translateX(100%)' : 'translateX(0)' }} />
          </div>

          {/* 패널 본문 */}
          <div style={s.panelBody} ref={panelBodyRef}>
            {tab === 'feedback' && feedbacks.map((f, idx) => (
              <div key={f.id} data-id={f.id} className="fb-item"
                style={{ ...s.fbItem, borderColor: selectedFeedback === f.id ? '#52a394' : 'rgba(255,255,255,0.08)' }}
                onClick={() => { jumpToFeedback(f); setNewFbIds(prev => prev.filter(id => id !== f.id)); }}>
                <div style={s.fbHead}>
                  <div style={s.fbNumBadge}>{idx + 1}</div>
                  <div style={s.fbAuthorWrap}>
                    <div style={s.fbAuthorRow}>
                      <span style={s.fbAuthorName}>{f.author_name}</span>
                      {newFbIds.includes(f.id) && <span style={s.fbNewBadge}>새 피드백</span>}
                      <div style={s.fbDateWrap}>
                        <span style={s.fbDate}>{f.created_at?.slice(0,16).replace(/-/g,'/')}</span>
                        <div style={s.fbDeleteRow}>
                          <span style={s.fbDateSpacer}>{f.created_at?.slice(0,5)}/</span>
                          <button className="fb-del-btn" style={{ ...s.fbDeleteBtn, position: 'relative' }} onClick={e => { e.stopPropagation(); setDeleteTarget({ id: f.id, type: 'feedback' }); }}>
                            {TRASH_SVG}
                            <span className="del-tooltip">삭제</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div style={s.fbTc}>{f.timecode}</div>
                  </div>
                </div>
                <div style={s.fbTxt}>{f.content}</div>
                {f.comments?.length > 0 && (
                  <div style={s.commentList}>
                    {f.comments.map(c => (
                      <div key={c.id} className="comment-item" style={s.commentItem}>
                        <div style={s.commentHeader}>
                          <span style={s.commentAuthor}>{c.author_name}</span>
                          <div style={s.commentDateWrap}>
                            <span style={s.commentDate}>{c.created_at?.slice(0,16).replace(/-/g,'/')}</span>
                            <div style={s.commentDelRow}>
                              <span style={s.commentDateSpacer}>{c.created_at?.slice(0,5)}/</span>
                              <button className="comment-del-btn" style={{ ...s.commentDelBtn, position: 'relative' }} onClick={e => { e.stopPropagation(); setDeleteTarget({ id: c.id, type: 'comment' }); }}>
                                {TRASH_SVG}
                                <span className="del-tooltip">삭제</span>
                              </button>
                            </div>
                          </div>
                        </div>
                        <div style={s.commentTxt}>{c.content}</div>
                      </div>
                    ))}
                  </div>
                )}
                <button style={s.fbReplyBtn} onClick={e => { e.stopPropagation(); jumpToFeedback(f); setCommentOpen(p => ({ ...p, [f.id]: !p[f.id] })); }}>
                  💬 댓글 달기
                </button>
                {commentOpen[f.id] && (
                  <div style={s.commentBox} onClick={e => e.stopPropagation()}>
                    <textarea style={s.commentInput} placeholder="댓글을 입력하세요..." value={commentText[f.id] || ''} onChange={e => setCommentText(p => ({ ...p, [f.id]: e.target.value }))} onInput={autoGrow} ref={el => { if (el && !commentText[f.id]) el.style.height = 'auto'; }} autoFocus rows={3} />
                    <div style={s.commentActions}>
                      <button style={s.commentBtn} onClick={() => setCommentOpen(p => ({ ...p, [f.id]: false }))}>취소</button>
                      <button style={{ ...s.commentBtn, ...s.commentBtnPrimary }} onClick={() => handleAddComment(f.id)}>저장</button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {tab === 'version' && versions.map((v, i) => (
              <div key={v.id} style={s.verPanelRow} onClick={() => handleVersionSelect(v)}>
                <div style={s.verPanelThumb}>{v.thumbnail_path ? <img src={v.thumbnail_path} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} /> : '▶'}</div>
                <div style={s.verPanelInfo}>
                  <div style={s.verPanelNum}>V{v.version_number}{i === 0 && <span style={s.verLatestTag}> 최신</span>}</div>
                  <div style={s.verPanelFile}>{truncate(v.original_name ? `v${v.version_number}_${v.original_name.replace(/\.[^.]+$/, '')}` : `v${v.version_number}_${videoNameClean}`)}</div>
                  <div style={s.verPanelDate}>{v.uploaded_at?.slice(0,10).replace(/-/g,'/')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 삭제 확인 */}
      {deleteTarget && (
        <div style={s.overlay} onClick={() => setDeleteTarget(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>{deleteTarget.type === 'feedback' ? '피드백을 삭제하시겠습니까?' : deleteTarget.type === 'comment' ? '댓글을 삭제하시겠습니까?' : '버전을 삭제하시겠습니까?'}</div>
            <div style={s.modalDesc}>이 작업은 되돌릴 수 없습니다.</div>
            <div style={s.modalActions}>
              <button style={s.modalBtnCancel} onClick={() => setDeleteTarget(null)}>취소</button>
              <button style={s.modalBtnDanger} onClick={deleteTarget.type === 'feedback' ? handleDeleteFeedback : deleteTarget.type === 'comment' ? handleDeleteComment : handleDeleteVersion}>삭제</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap: { height: '100vh', background: '#191919', fontFamily: "'Pretendard', sans-serif", display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  topbar: { borderTop: '1px solid rgba(255,255,255,0.2)', borderBottom: '1px solid rgba(255,255,255,0.2)', padding: '0 18px', height: 56, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  divider: { width: 1, height: 18, background: 'rgba(255,255,255,0.15)', flexShrink: 0 },
  topbarPath: { display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0 },
  topbarWs: { fontSize: '11pt', fontWeight: 500, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.5px', cursor: 'pointer', whiteSpace: 'nowrap' },
  topbarArrow: { color: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', flexShrink: 0 },
  topbarCurrent: { fontSize: '11pt', fontWeight: 400, color: '#fff', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  topbarRight: { display: 'flex', alignItems: 'center', gap: 24, flexShrink: 0 },
  verBtn: { display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: 'none', color: '#fff', fontSize: '11pt', cursor: 'pointer', fontFamily: 'inherit', padding: '2px 0' },
  popup: { position: 'absolute', top: 'calc(100% + 6px)', left: 0, background: '#2a2a2a', border: '0.56px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: 6, zIndex: 300, boxShadow: '0 8px 24px rgba(0,0,0,0.6)', minWidth: 320 },
  popupRight: { left: 'auto', right: 0, minWidth: 160 },
  popDivider: { height: 1, background: 'rgba(255,255,255,0.07)', margin: '3px 0' },
  verRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', borderRadius: 7, cursor: 'pointer' },
  verThumb: { width: 44, height: 30, background: '#383838', borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'rgba(255,255,255,0.25)', fontSize: 9 },
  verNum: { fontSize: '11pt', fontWeight: 600, color: '#fff' },
  verLatestTag: { fontSize: 12, color: '#5755df', marginLeft: 4 },
  verFile: { fontSize: '10pt', color: '#fff', marginTop: 1 },
  verDate: { fontSize: '9pt', color: 'rgba(255,255,255,0.4)' },
  verDel: { color: 'rgba(255,255,255,0.2)', cursor: 'pointer', background: 'none', border: 'none', display: 'flex', alignItems: 'center' },
  compareBtn: { display: 'flex', alignItems: 'center', gap: 5, background: 'transparent', border: 'none', color: '#fff', fontSize: '11pt', cursor: 'pointer', fontFamily: 'inherit', padding: '2px 0' },
  statusBtn: { display: 'flex', alignItems: 'center', gap: 5, background: 'transparent', border: '0.56px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '5px 10px', color: '#fff', fontSize: '11pt', cursor: 'pointer', fontFamily: 'inherit', minWidth: 110 },
  sDot: { width: 11, height: 11, borderRadius: '50%', flexShrink: 0, display: 'inline-block' },
  statusItem: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 7, cursor: 'pointer', fontSize: '11pt', color: '#fff' },
  moreBtn: { width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '0.56px solid rgba(255,255,255,0.2)', borderRadius: 8, color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '14pt' },
  moreItem: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 7, cursor: 'pointer', fontSize: '10pt', color: '#fff' },
  main: { display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' },
  videoCol: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding: 14, gap: 8, overflow: 'hidden' },
  playerArea: { flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  playerWrap: { position: 'relative', background: '#000', overflow: 'hidden', cursor: 'crosshair', userSelect: 'none', width: '100%', aspectRatio: '16/9', maxHeight: '100%' },
  playerInner: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  playCircle: { width: 56, height: 56, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)', cursor: 'pointer' },
  video: { width: '100%', height: '100%', objectFit: 'contain', display: 'block' },
  annPopup: { position: 'absolute', background: '#28292a', borderRadius: 10, padding: 12, width: 286, zIndex: 20, boxShadow: '0 8px 24px rgba(0,0,0,0.55)' },
  annHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  annName: { fontSize: '11pt', fontWeight: 300, color: 'rgba(255,255,255,0.5)' },
  annDate: { fontSize: '9pt', color: 'rgba(255,255,255,0.4)' },
  annTextarea: { width: '100%', fontSize: '12pt', background: '#1e1e1f', border: '0.5px solid #3a3a3a', borderRadius: 6, padding: '6px 8px', resize: 'none', minHeight: 60, outline: 'none', color: '#fff', fontFamily: 'inherit', boxSizing: 'border-box', overflow: 'hidden' },
  annActions: { display: 'flex', justifyContent: 'flex-end', gap: 5, marginTop: 6 },
  annBtn: { fontSize: '11pt', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.4)', fontFamily: 'inherit' },
  annBtnPrimary: { background: '#74acc8', color: '#fff' },
  controls: { flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 5 },
  progressWrap: { width: '100%', height: 20, margin: '-8px 0', display: 'flex', alignItems: 'center', cursor: 'pointer', position: 'relative' },
  progressTrack: { width: '100%', height: 4.5, background: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden', transition: 'height .15s' },
  markerRow: { position: 'relative', height: 10, width: '100%' },
  fbMarker: { position: 'absolute', width: 9, height: 9, background: '#f97316', borderRadius: '50%', top: 0, transform: 'translateX(-50%)', cursor: 'pointer', border: '1.5px solid rgba(0,0,0,0.3)' },
  ctrlRow: { display: 'flex', alignItems: 'center' },
  ctrlSide: { display: 'flex', alignItems: 'center', gap: 6, flex: 1 },
  ctrlCenter: { display: 'flex', alignItems: 'center', gap: 16 },
  ctrlBtn: { background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 3, outline: 'none' },
  timecode: { fontSize: '18pt', color: 'rgba(255,255,255,0.4)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  volPopup: { position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)', background: '#2a2a2a', border: '0.56px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: '10px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: 36, zIndex: 100 },
  volSlider: { writingMode: 'vertical-lr', direction: 'rtl', width: 4, height: 80, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none', outline: 'none', borderRadius: 2 },
  settingsPopup: { position: 'absolute', bottom: 'calc(100% + 6px)', right: 0, background: '#2a2a2a', border: '0.56px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: 6, width: 160, boxShadow: '0 8px 24px rgba(0,0,0,0.6)', zIndex: 100 },
  setLabel: { fontSize: 8, color: 'rgba(255,255,255,0.3)', padding: '4px 8px 2px', fontWeight: 600 },
  setItem: { fontSize: 9, padding: '6px 10px', borderRadius: 6, color: 'rgba(255,255,255,0.5)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  sidePanel: { width: 394, flexShrink: 0, borderLeft: '0.56px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' },
  slidePanel: { position: 'absolute', inset: 0, background: '#1e1e1f', display: 'flex', flexDirection: 'column', transition: 'transform .25s ease', zIndex: 10 },
  slidePanelHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '0.56px solid rgba(255,255,255,0.1)', flexShrink: 0 },
  slidePanelTitle: { fontSize: '12pt', color: '#fff', fontWeight: 500 },
  slideClose: { background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center' },
  slideBody: { padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 16, flex: 1 },
  shareField: { display: 'flex', flexDirection: 'column', gap: 6 },
  shareLabel: { display: 'block', fontSize: '10pt', color: 'rgba(255,255,255,0.4)', marginBottom: 6 },
  shareInput: { width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '9px 12px', fontSize: '11pt', color: '#fff', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
  shareActions: { display: 'flex', justifyContent: 'flex-end', gap: 8 },
  shareBtn: { fontSize: '10pt', padding: '7px 16px', borderRadius: 8, cursor: 'pointer', border: '0.56px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.5)', fontFamily: 'inherit' },
  shareBtnPrimary: { background: '#5755df', color: '#fff', borderColor: '#5755df' },
  shareUrlBox: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 12px', fontSize: '10pt', color: 'rgba(255,255,255,0.6)', wordBreak: 'break-all', lineHeight: 1.5 },
  dropZone: { border: '1.5px dashed rgba(255,255,255,0.2)', borderRadius: 12, padding: '28px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'rgba(255,255,255,0.2)', flexShrink: 0 },
  dropLabel: { fontSize: '11pt', color: 'rgba(255,255,255,0.4)', textAlign: 'center', lineHeight: 1.6 },
  dropSub: { fontSize: '9pt', color: 'rgba(255,255,255,0.2)' },
  fileList: { display: 'flex', flexDirection: 'column', gap: 6 },
  fileRow: { background: 'rgba(255,255,255,0.04)', border: '0.56px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '9px 12px', display: 'flex', alignItems: 'center', gap: 10 },
  fileInfo: { flex: 1, minWidth: 0 },
  fileName: { fontSize: '10pt', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileSize: { fontSize: '9pt', color: 'rgba(255,255,255,0.3)', marginTop: 2 },
  progressBarBg: { width: '100%', height: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2, marginTop: 5 },
  progressBarFill: { height: '100%', borderRadius: 2, transition: 'width .3s' },
  fileRemove: { background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center' },
  uploadActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 'auto' },
  uploadBtn: { fontSize: '10pt', padding: '7px 16px', borderRadius: 8, cursor: 'pointer', border: '0.56px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.5)', fontFamily: 'inherit' },
  uploadBtnPrimary: { background: '#5755df', color: '#fff', borderColor: '#5755df' },
  panelTabs: { display: 'flex', borderBottom: '0.56px solid rgba(255,255,255,0.1)', flexShrink: 0, position: 'relative' },
  panelTab: { flex: 1, padding: 12, textAlign: 'center', fontSize: '11pt', cursor: 'pointer' },
  tabIndicator: { position: 'absolute', bottom: 0, left: 0, width: '50%', height: 3, background: '#fff', borderRadius: '2px 2px 0 0', transition: 'transform .25s cubic-bezier(.4,0,.2,1)' },
  panelBody: { flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8, scrollbarWidth: 'none', msOverflowStyle: 'none' },
  fbItem: { background: 'rgba(255,255,255,0.03)', border: '0.56px solid', borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6, cursor: 'pointer' },
  fbHead: { display: 'flex', alignItems: 'center', gap: 8 },
  fbNumBadge: { width: 40, height: 40, borderRadius: '50%', background: '#ffbc00', color: '#fff', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  fbAuthorWrap: { flex: 1, minWidth: 0 },
  fbAuthorRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 },
  fbAuthorName: { fontSize: '12pt', color: '#f7f7f7', fontWeight: 400, whiteSpace: 'nowrap' },
  fbNewBadge: { fontSize: 14, padding: '3px 11px', borderRadius: 20, background: 'rgba(87,85,223,0.2)', color: '#a5a3ff', border: '1px solid rgba(87,85,223,0.3)', flexShrink: 0, marginLeft: 6, whiteSpace: 'nowrap' },
  fbDateWrap: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 },
  fbDate: { fontSize: '9pt', color: '#939393', whiteSpace: 'nowrap' },
  fbDeleteRow: { display: 'flex', alignItems: 'center' },
  fbDateSpacer: { fontSize: '9pt', visibility: 'hidden', whiteSpace: 'nowrap' },
  fbDeleteBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center' },
  fbTc: { fontSize: '9pt', color: '#73add8', marginTop: 2, fontVariantNumeric: 'tabular-nums' },
  fbTxt: { fontSize: '12pt', color: '#fff', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  fbReplyBtn: { fontSize: '9pt', color: 'rgba(255,255,255,0.25)', background: 'none', border: 'none', borderTop: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer', padding: '6px 0 0 0', width: '100%', display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: 'inherit' },
  commentList: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 },
  commentItem: { background: 'rgba(255,255,255,0.04)', borderRadius: 6, padding: '7px 10px' },
  commentHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 },
  commentAuthor: { fontSize: '9pt', color: 'rgba(255,255,255,0.5)', fontWeight: 500 },
  commentDateWrap: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 },
  commentDate: { fontSize: '8pt', color: 'rgba(255,255,255,0.25)' },
  commentDelRow: { display: 'flex', alignItems: 'center' },
  commentDateSpacer: { fontSize: '9pt', visibility: 'hidden' },
  commentDelBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center' },
  commentTxt: { fontSize: '12pt', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  commentBox: { display: 'flex', flexDirection: 'column', gap: 5, marginTop: 2 },
  commentInput: { width: '100%', fontSize: '12pt', background: 'rgba(255,255,255,0.05)', border: '0.56px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '6px 8px', resize: 'none', minHeight: 60, outline: 'none', color: '#fff', fontFamily: 'inherit', boxSizing: 'border-box', overflow: 'hidden' },
  commentActions: { display: 'flex', justifyContent: 'flex-end', gap: 5 },
  commentBtn: { fontSize: '11pt', padding: '3px 8px', borderRadius: 5, cursor: 'pointer', border: '0.56px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.4)', fontFamily: 'inherit' },
  commentBtnPrimary: { background: 'rgba(87,85,223,0.3)', color: '#a5a3ff', borderColor: 'rgba(87,85,223,0.4)' },
  verPanelRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '0.56px solid rgba(255,255,255,0.07)', cursor: 'pointer' },
  verPanelThumb: { width: 36, height: 36, borderRadius: 6, background: '#2a2a2a', border: '0.56px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '10pt', flexShrink: 0 },
  verPanelInfo: { flex: 1, minWidth: 0 },
  verPanelNum: { fontSize: '10pt', color: '#fff', fontWeight: 600 },
  verPanelFile: { fontSize: '10pt', color: '#fff', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  verPanelDate: { fontSize: '9pt', color: 'rgba(255,255,255,0.25)', marginTop: 2 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#303030', border: '0.56px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: 24, width: 340, display: 'flex', flexDirection: 'column', gap: 12 },
  modalTitle: { fontSize: '13pt', fontWeight: 600, color: '#fff' },
  modalDesc: { fontSize: '10pt', color: 'rgba(255,255,255,0.4)' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 },
  modalBtnCancel: { fontSize: '10pt', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', border: '0.56px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.5)', fontFamily: 'inherit' },
  modalBtnDanger: { fontSize: '10pt', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', border: 'none', background: '#e74c3c', color: '#fff', fontFamily: 'inherit' },
};
